import { useContext } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  CloseMdIcon,
  IconButton,
  MergeViewsSmIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { AccountDrawerContext } from './contexts';
import { getTaskSplitHeader } from './taskSplitViewHeaders';

interface TaskSplitPanelProps {
  isOpen: boolean;
  onPortalNodeChange: (node: HTMLDivElement | null) => void;
}

// The task view itself is portaled in from pages/accountDrawer rather than
// rendered here — see useTaskViewPortal for why.
export const TaskSplitPanel = ({
  isOpen,
  onPortalNodeChange,
}: TaskSplitPanelProps) => {
  const context = useContext(AccountDrawerContext);
  if (context === undefined) {
    throw new Error(
      'TaskSplitPanel must be used within a AccountDrawerProvider',
    );
  }
  const {
    drawerContent,
    updateDrawerContent,
    callNeedsDiscardConfirm,
    openDiscardCallModal,
    linkedinTabLabel,
  } = context;

  const header = getTaskSplitHeader(
    drawerContent?.modalVisible,
    linkedinTabLabel,
  );

  const closeTask = () => {
    const doClose = () =>
      updateDrawerContent({ modalVisible: undefined, isTaskSplitView: false });

    if (callNeedsDiscardConfirm) {
      openDiscardCallModal({ onConfirm: doClose });
    } else {
      doClose();
    }
  };

  const mergeBackIntoAccountDrawer = () => {
    if (!drawerContent?.modalVisible) return;
    updateDrawerContent({
      isTaskSplitView: false,
      tab: drawerContent.modalVisible,
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="task-split-panel"
          initial={{ width: 0 }}
          animate={{ width: '50%' }}
          exit={{ width: 0 }}
          className="fixed left-0 top-0 bottom-0 bg-white z-50 flex flex-col overflow-hidden"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 18,
              paddingLeft: 18,
              paddingTop: 18,
            }}
          >
            <IconButton
              label="Close task"
              icon={CloseMdIcon}
              shape="rounded"
              variant="light"
              tone="lightGray"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                closeTask();
              }}
            />

            <div className="flex items-center gap-3">
              <div className="size-8 shrink-0 rounded-full bg-primary-100 text-green-600 grid place-content-center">
                {header?.icon}
              </div>
              <p className="text-2xl font-bold truncate">{header?.label}</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    label="Merge views"
                    icon={MergeViewsSmIcon}
                    variant="ghost"
                    shape="rounded"
                    size="xs"
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      mergeBackIntoAccountDrawer();
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Merge views</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div
            ref={onPortalNodeChange}
            className="min-w-0 min-h-0 flex-1 flex"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
